/**
 * useCategoryOrder
 *
 * Sorts the provided category list by FRESHNESS — the category whose content
 * was most recently added or moved into it appears first (right after the
 * pinned "All" entry).  Freshness per category is the MAX of
 * `categorySetAt ?? createdAt ?? tarobase_created_at ?? 0` across all
 * wallpapers that belong to it.
 *
 * The old click-frequency ordering (localStorage `categoryClickCounts`) has
 * been fully replaced.  `incrementCategory` is kept as a no-op so existing
 * call sites compile without changes; callers may stop calling it.
 */

import { useMemo } from 'react';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';

interface UseCategoryOrderResult {
  /** Category list sorted by descending freshness; "All" is always first. */
  sortedCategories: string[];
  /**
   * No-op — retained for backward compatibility with existing call sites.
   * Click counts no longer influence ordering.
   */
  incrementCategory: (name: string) => void;
}

function noop(_name: string): void {}

export function useCategoryOrder(
  defaultCategories: string[],
  wallpapers: WallpapersResponse[] = [],
): UseCategoryOrderResult {
  const sortedCategories = useMemo(() => {
    // Build a per-category recency map: max(categorySetAt ?? createdAt ?? tarobase_created_at ?? 0)
    const recency: Record<string, number> = {};
    for (const w of wallpapers) {
      if (!w.category) continue;
      const key = w.category.toLowerCase();
      const ts = w.categorySetAt ?? w.createdAt ?? w.tarobase_created_at ?? 0;
      if ((recency[key] ?? 0) < ts) recency[key] = ts;
    }

    const rest = defaultCategories.filter(c => c !== 'All');

    // Stable descending sort by recency — ties preserve original list order
    const sorted = rest
      .map((cat, originalIndex) => ({
        cat,
        ts: recency[cat.toLowerCase()] ?? 0,
        originalIndex,
      }))
      .sort((a, b) => {
        if (b.ts !== a.ts) return b.ts - a.ts;
        return a.originalIndex - b.originalIndex;
      })
      .map(({ cat }) => cat);

    if (defaultCategories.includes('All')) {
      return ['All', ...sorted];
    }
    return sorted;
  }, [defaultCategories, wallpapers]);

  return { sortedCategories, incrementCategory: noop };
}
